import { SpinResponse } from '@cgs/data-access';
import {
  AbstractGameConfig,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IGameConfigProvider,
  IGameStateMachineProvider,
  ISlotGameEngineProvider,
  ReelsEngine,
  RegularSpinsSoundModel,
  RegularSpinsSoundModelComponent,
  T_IGameConfigProvider,
  T_ISlotGameEngineProvider,
  T_RegularSpinsSoundModelComponent,
  WaitForAction,
} from '@cgs/machines';
import { BuildAction } from '@cgs/shared';
import { Container, EmptyAction, FunctionAction, IntervalAction, SequenceAction } from '@cgs/wge';
import { CorsicaAnticipationProvider } from './corsica-anticipation.provider';

export class CorsicaStopAnticipationAction extends BuildAction {
  private gameStateMachine: GameStateMachine<SpinResponse>;
  private reelsEngine: ReelsEngine;
  private regularSpinSoundModel: RegularSpinsSoundModel;
  private gameConfig: AbstractGameConfig;

  private anticipationProvider: CorsicaAnticipationProvider;

  constructor(
    container: Container,
    private readonly spinDelayFactor: number
  ) {
    super();

    this.anticipationProvider = new CorsicaAnticipationProvider(container);

    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.reelsEngine =
      container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider).reelsEngine;

    this.regularSpinSoundModel = container.resolve<RegularSpinsSoundModelComponent>(
      T_RegularSpinsSoundModelComponent
    ).regularSpinSoundModel;

    this.gameConfig = container.resolve<IGameConfigProvider>(T_IGameConfigProvider).gameConfig;
  }

  public buildAction(): IntervalAction {
    const actions: IntervalAction[] = [];
    void this.reelsEngine.slotsStoped.first.then(() => this.handleSlotStopped());

    const waitAction = new WaitForAction(this.reelsEngine.slotsStoped);
    waitAction.subscribe();

    for (let reel = 0; reel < this.reelsEngine.ReelConfig.reelCount; ++reel) {
      if (reel > 0 && this.spinDelayFactor > 0) {
        actions.push(
          new EmptyAction().withDuration(
            this.gameConfig.regularSpinConfig.spinStopDelay * this.spinDelayFactor
          )
        );
      }
      actions.push(this.stop(reel, this.gameStateMachine.curResponse.viewReels[reel]));

      if (!this.gameStateMachine.curResponse.isFreeSpins) {
        actions.push(this.anticipationProvider.anticipationAction(reel));
      }
    }

    actions.push(waitAction);

    return new SequenceAction(actions);
  }

  private stop(reel: number, winTapes: number[]): IntervalAction {
    return new FunctionAction(() => this.reelsEngine.stop(reel, winTapes));
  }

  private handleSlotStopped(): void {
    const isWin =
      this.gameStateMachine.curResponse.winLines.length > 0 ||
      this.gameStateMachine.curResponse.winPositions.length > 0;

    isWin
      ? this.regularSpinSoundModel.startSpinSound.GoToState('fade_out')
      : this.regularSpinSoundModel.startSpinSound.GoToState('stop_sound');

    this.anticipationProvider.handleSlotStopped();
  }
}
